import { Typography } from '@/components/ui/typography'
import FieldInfo from '@/components/form/FieldInfo'
import { Slider } from '@/components/ui/slider'
import { Label } from '@/components/ui/label'
import { useIsReadOnly } from '@/lib/casl'
import { useFieldContext, useFormContext } from '@/lib/form-contexts'
import { MediaUploadUsage } from '@/lib/enums/MediaUploadUsage'
import { PhotoManagerMode } from '@/lib/photo-manager-config'
import { Globe, Image, Type } from 'lucide-react'
import LogoDisplayForm from './LogoDisplayForm'
import ThemeForm from './ThemeForm'
import { cornerStyleOptions, fontPairingOptions } from './storefrontStyleOptions'

const cornerStyleIndexes = new Map(cornerStyleOptions.map((option, index) => [option.id, index]))

function CornerStyleSliderInput() {
  const field = useFieldContext()
  const { isReadOnly } = useIsReadOnly()
  const selectedIndex =
    cornerStyleIndexes.get(field.state.value) ?? cornerStyleIndexes.get('classic')
  const selectedOption = cornerStyleOptions[selectedIndex] ?? cornerStyleOptions[0]
  const isDisabled = isReadOnly || field.state.disabled

  const handleValueChange = (value) => {
    const nextIndex = Number(value?.[0])
    const nextOption = cornerStyleOptions[nextIndex] ?? selectedOption

    field.handleChange(nextOption.id)
  }

  return (
    <div className="flex flex-col gap-y-3">
      <Label htmlFor={field.name}>Corner Style</Label>

      <div className="px-1 pt-2">
        <Slider
          id={field.name}
          min={0}
          max={cornerStyleOptions.length - 1}
          step={1}
          value={[selectedIndex]}
          onValueChange={handleValueChange}
          onValueCommit={field.handleBlur}
          aria-label="Corner Style"
          thumbAriaValueText={selectedOption.name}
          disabled={isDisabled}
        />
        <div className="mt-2 flex items-center justify-between text-xs font-medium text-muted-foreground">
          <span>Square</span>
          <span>Rounded</span>
        </div>
      </div>

      <FieldInfo />
    </div>
  )
}

const BrandStyleForm = ({ showThemePreview = true, showLogoSizePreview = false }) => {
  const form = useFormContext()
  return (
    <div className="flex flex-col gap-y-8">
      <div className="w-full @container">
        <div className="grid w-full grid-cols-1 gap-8 @2xl:grid-cols-2">
          <form.AppField name="brand.media.header_logo">
            {(field) => (
              <field.MediaUploadInput
                photoManagerMode={PhotoManagerMode.UploadOnlySaved}
                label={
                  <Typography variant="h4" className="flex items-center gap-2">
                    <Image className="size-5" /> Header Logo
                  </Typography>
                }
                description="Used in the storefront header."
              />
            )}
          </form.AppField>
        </div>
      </div>

      <LogoDisplayForm showLogoSizePreview={showLogoSizePreview} />

      <div className="w-full @container">
        <div className="grid w-full grid-cols-1 gap-8 @2xl:grid-cols-2">
          <div className="w-full">
            <form.AppField name="brand.media.favicon">
              {(field) => (
                <field.MediaUploadInput
                  uploadUsage={MediaUploadUsage.FaviconIcon.Value}
                  label={
                    <Typography variant="h4" className="flex items-center gap-2">
                      <Globe className="size-5" /> Custom Favicon
                    </Typography>
                  }
                  description="Shown in browser tabs and bookmarks for your storefront."
                />
              )}
            </form.AppField>
          </div>
        </div>
      </div>

      <div className="w-full @container">
        <Typography variant="h4" className="mb-3 flex items-center gap-2">
          <Type className="size-5" /> Storefront Style
        </Typography>
        <div className="grid w-full grid-cols-1 gap-4 @2xl:grid-cols-2">
          <form.AppField name="brand.font_pairing">
            {(field) => (
              <field.SelectInput
                options={fontPairingOptions}
                label="Font Style"
                description="Choose a curated type style for headings and page text."
              />
            )}
          </form.AppField>
          <form.AppField name="brand.corner_style">
            {() => <CornerStyleSliderInput />}
          </form.AppField>
        </div>
      </div>

      {/* THEME FORM  */}
      <ThemeForm showPreview={showThemePreview} />
    </div>
  )
}

export default BrandStyleForm
